import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { ArrowLeft, Building2, Check, ChevronRight, CircleDot, Trophy, User, X } from 'lucide-react';

type ProfileKey = 'joueur' | 'club' | 'organisateur';

const profiles: Record<
  ProfileKey,
  {
    icon: React.ElementType;
    title: string;
    desc: string;
    route: string;
    color: string;
    iconBg: string;
    cardBorder: string;
    cardHover: string;
    confirmBtn: string;
    canDo: string[];
    cannotDo: string[];
  }
> = {
  joueur: {
    icon: User,
    title: 'Je suis joueur',
    desc: "Rechercher des concours, m'inscrire en équipe",
    route: '/',
    color: 'text-emerald-600',
    iconBg: 'bg-emerald-100',
    cardBorder: 'border-emerald-100 hover:border-emerald-300',
    cardHover: 'hover:bg-emerald-50',
    confirmBtn: 'bg-emerald-600 hover:bg-emerald-700',
    canDo: [
      "Rechercher et s'inscrire à des concours",
      'Rejoindre un club comme membre',
      'Consulter votre profil joueur et vos résultats',
      'Suivre votre palmarès et vos statistiques',
    ],
    cannotDo: [
      'Créer ou administrer un club (rôle Gestionnaire requis)',
      'Organiser des concours (rôle Organisateur requis)',
      "Gérer des inscriptions d'équipes en tant qu'administrateur",
    ],
  },
  club: {
    icon: Building2,
    title: 'Je gère un club',
    desc: 'Créer mon club, gérer mes membres et équipes',
    route: ROUTES.CREATE_CLUB_ONBOARDING,
    color: 'text-blue-600',
    iconBg: 'bg-blue-100',
    cardBorder: 'border-blue-100 hover:border-blue-300',
    cardHover: 'hover:bg-blue-50',
    confirmBtn: 'bg-blue-600 hover:bg-blue-700',
    canDo: [
      'Créer et administrer votre club',
      'Gérer les membres et les équipes du club',
      'Inscrire vos équipes à des concours',
      'Accéder au tableau de bord de gestion du club',
    ],
    cannotDo: [
      'Organiser vos propres concours (rôle Organisateur requis)',
      "Participer comme simple joueur dans d'autres clubs",
      'Gérer plusieurs clubs simultanément avec ce compte',
    ],
  },
  organisateur: {
    icon: Trophy,
    title: "J'organise des concours",
    desc: 'Créer et gérer des compétitions de pétanque',
    route: ROUTES.CREATE_ORG_ONBOARDING,
    color: 'text-orange-600',
    iconBg: 'bg-orange-100',
    cardBorder: 'border-orange-100 hover:border-orange-300',
    cardHover: 'hover:bg-orange-50',
    confirmBtn: 'bg-orange-600 hover:bg-orange-700',
    canDo: [
      'Créer et gérer une organisation sportive',
      'Organiser des concours et compétitions',
      'Affilier des clubs à votre organisation',
      'Accéder aux outils de gestion avancés et rapports',
    ],
    cannotDo: [
      'Administrer directement un club affilié (rôle distinct)',
      'Participer comme joueur individuel à des concours',
      'Modifier les effectifs des clubs de votre organisation',
    ],
  },
};

export default function OAuthOnboarding() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<ProfileKey | null>(null);
  const profile = selected ? profiles[selected] : null;

  return (
    <section className='min-h-screen bg-gradient-to-br from-emerald-900 to-emerald-500 flex flex-col items-center justify-center px-6 pt-20 pb-10'>
      <div className='flex items-center gap-3 mb-8'>
        <CircleDot className='w-10 h-10 text-white' />
        <span className='text-2xl font-extrabold text-white'>BoulesConnect</span>
      </div>

      <div className='w-full max-w-md bg-white rounded-3xl p-8 shadow-xl'>
        <div className='flex flex-col items-center text-center mb-6'>
          <div className='w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mb-4'>
            <Check className='w-8 h-8 text-emerald-600' />
          </div>
          <h2 className='text-2xl font-extrabold text-slate-900 mb-1'>Bienvenue !</h2>
          <p className='text-slate-500 text-sm'>
            {selected ? 'Confirmez votre choix de profil' : 'Quel est votre profil sur BoulesConnect ?'}
          </p>
        </div>

        {/* Step 1 — profile choice */}
        {!selected && (
          <div className='space-y-3'>
            {(Object.entries(profiles) as [ProfileKey, (typeof profiles)[ProfileKey]][]).map(([key, p]) => (
              <button
                key={key}
                type='button'
                onClick={() => setSelected(key)}
                className={`w-full flex items-center gap-4 p-4 rounded-2xl border transition-all group text-left ${p.cardBorder} ${p.cardHover} cursor-pointer`}
              >
                <div className={`w-10 h-10 ${p.iconBg} rounded-xl flex items-center justify-center shrink-0`}>
                  <p.icon className={`w-5 h-5 ${p.color}`} />
                </div>
                <div className='flex-1'>
                  <span className='font-bold text-slate-900 text-sm block'>{p.title}</span>
                  <span className='text-xs text-slate-500'>{p.desc}</span>
                </div>
                <ChevronRight className='w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors' />
              </button>
            ))}
          </div>
        )}

        {/* Step 2 — confirmation */}
        {selected && profile && (
          <div>
            <div className={`flex items-center gap-3 p-3 rounded-xl ${profile.iconBg} mb-5`}>
              <div className='w-9 h-9 bg-white/60 rounded-lg flex items-center justify-center shrink-0'>
                <profile.icon className={`w-5 h-5 ${profile.color}`} />
              </div>
              <span className={`font-bold text-sm ${profile.color}`}>{profile.title}</span>
            </div>

            <div className='mb-4'>
              <p className='text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2'>
                Avec ce profil, vous pourrez
              </p>
              <ul className='space-y-1.5'>
                {profile.canDo.map((item, i) => (
                  <li key={i} className='flex items-start gap-2 text-sm text-slate-700'>
                    <Check className='w-4 h-4 text-emerald-500 shrink-0 mt-0.5' />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className='mb-6 p-3 bg-slate-50 rounded-xl border border-slate-100'>
              <p className='text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2'>
                Ce qui ne sera pas possible
              </p>
              <ul className='space-y-1.5'>
                {profile.cannotDo.map((item, i) => (
                  <li key={i} className='flex items-start gap-2 text-sm text-slate-500'>
                    <X className='w-4 h-4 text-red-400 shrink-0 mt-0.5' />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className='flex gap-3'>
              <button
                type='button'
                onClick={() => setSelected(null)}
                className='flex items-center gap-1.5 px-4 py-3 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer'
              >
                <ArrowLeft className='w-4 h-4' />
                Retour
              </button>
              <button
                type='button'
                onClick={() => navigate(profile.route, { replace: true })}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-white font-bold text-sm transition-colors ${profile.confirmBtn} cursor-pointer`}
              >
                Confirmer et continuer
                <ChevronRight className='w-4 h-4' />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
